import React, { useRef, useState, useEffect, ReactNode, useCallback } from "react";
import { Table as AntTable } from 'antd';
import { Button } from "../Buttons";
import { Switch } from 'antd';
import { ColumnsType } from "antd/es/table";
import { DownloadIcon } from "../Icons/iconImages";

import './styles.css';
import { flattenDataRows } from "../../helper";

interface TableProps<T> {
  data: T[];
  renderHeader: () => ReactNode;
  renderRow: (row: T, index: number) => ReactNode;
  rowHeight?: number;
  buffer?: number;
  exapandable?: boolean;
}

interface TableTeamProps {
  data: TeamMember[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  updateToggle: (checked: any) => void;
}

interface TeamMember {
  id: string;
  name: string;
  email: string;
  profile_picture: string;
  admin: boolean;
}

interface ViewRawDataProps {
  integrationId?: 'googleanalytics' | 'shopify' | 'meta' | 'none';
  dataSource?: any;
}

const Table = <T,>({
  data = [],
  renderHeader,
  renderRow,
  rowHeight = 50,
  buffer = 5,
  exapandable = true,
}: TableProps<T>): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewportHeight, setViewportHeight] = useState<number>(0);
  const [scrollTop, setScrollTop] = useState<number>(0);


  const handleScroll = () => {
    if (containerRef.current) {
      setScrollTop(containerRef.current.scrollTop);
    }
  };

  const updateViewportHeight = () => {
    if (containerRef.current) {
      setViewportHeight(containerRef.current.clientHeight);
    }
  };

  useEffect(() => {
    updateViewportHeight();
    window.addEventListener("resize", updateViewportHeight);
    return () => {
      window.removeEventListener("resize", updateViewportHeight);
    };
  }, []);

  const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - buffer);
  const endIndex = Math.min(
    data.length,
    Math.ceil((scrollTop + viewportHeight) / rowHeight) + buffer
  );

  const visibleData = data.slice(startIndex, endIndex);
  const offsetY = startIndex * rowHeight;

  return (
    <div
      className="relative overflow-x-auto border-2 border-gray-200 rounded-xl"
      ref={containerRef}
      onScroll={handleScroll}
      style={{ height: "400px" }}
    >
      {/* Table */}
      <table className="w-full text-sm text-left rtl:text-right">
        {/* Header */}
        <thead className="text-gray-700 sticky top-0 z-10 capitalize border-b-2 border-gray-200 bg-gray-50">
          <tr>
            {renderHeader()}
          </tr>
        </thead>

        {/* Rows */}
        <tbody className="relative">
          <tr style={{ height: offsetY, visibility: "hidden" }}></tr>
          {visibleData.map((row, index) => (
            <React.Fragment key={index}>
              {renderRow(row, startIndex + index)}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface TableDataProps {
  data: [string, any][] | any[];
  isFetching: boolean;
  isError: boolean;
  columnWidth?: number | 'auto';
  pageSizeLimit?: number | 'no-limit';
  exapandable?: boolean;
  className?: string;
  scrollY?: string | number;
  onExpandClick?: () => void;
}

const getColumns = (dataSource: ViewRawDataProps['dataSource'], width: number | 'auto' = 'auto'): ColumnsType => {
  if (!dataSource) return [];

  const processedDataSource = flattenDataRows(dataSource);

  // Step 1: Collect all unique keys from all rows
  const allKeys = new Set<string>();
  processedDataSource.forEach(row => {
    Object.keys(row || {}).forEach(key => allKeys.add(key));
  });

  const validColumns = Array.from(allKeys).map((key) => {
    // Find the first non-object value to use for logic
    const exampleValue = processedDataSource.find(row => typeof row[key] !== 'object' && !Array.isArray(row[key]))?.[key];
    if (typeof exampleValue === 'object' || Array.isArray(exampleValue)) return null;

    return {
      title: key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()),
      dataIndex: key,
      key: key,
      width: width === 'auto' ? undefined : 200,
      render: (text: any) => {
        const isDateField = ['created_at', 'updated_at', 'published_at'].includes(key);
        const isHtmlField = key === 'body_html';
        const isNumberField = typeof text === 'number';
        const isObjectField = typeof text === 'object' && text !== null && !Array.isArray(text);

        let displayText = text;
        if (isDateField && text) {
          displayText = new Date(text).toLocaleString();
        } else if (isNumberField) {
          displayText = text.toLocaleString('en-US');
        } else if (isObjectField) {
          // Handle objects like Actions field
          displayText = Object.entries(text)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
        } else if (Array.isArray(text)) {
          // Check if array contains objects
          if (text.length > 0 && typeof text[0] === 'object') {
            // Render as a badge for arrays of objects
            return (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                {text.length} {text.length === 1 ? 'item' : 'items'}
              </span>
            );
          } else {
            displayText = text.join(', ');
          }
        } else if (text === null || text === undefined) {
          displayText = '';
        } else {
          displayText = String(text);
        }

        return isHtmlField ? (
          <div className="whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: text || '' }} />
        ) : (
          <p className="text-[#535862] text-sm font-normal">{displayText}</p>
        );
      },
      ellipsis: true,
    };
  }).filter((column): column is Exclude<typeof column, null> => column !== null);

  // Optional: if there's only one column, name it 'Analytics Data'
  if (validColumns?.length === 1) {
    return validColumns.map(({ title, ...rest }) => ({
      ...rest,
      title: 'Analytics Data',
    }));
  }

  return validColumns;
};

const TableData = ({ data, isFetching, isError, pageSizeLimit = 10, columnWidth = 'auto', exapandable, className, scrollY, onExpandClick }: TableDataProps) => {
  const dataSource = data ?
    (Array.isArray(data) ?
      data.map((item, index) => ({
        ...(typeof item === 'object' && item !== null ? item : {}),
        key: index.toString(),
      }))
      :
      Object.entries(data).map(([key, value], index) => ({
        [key]: value,
        key: key,
      }))
    ) : [];
  const [pageSize, setPageSize] = React.useState(pageSizeLimit);
  const columns = getColumns(dataSource, columnWidth).filter((column): column is ColumnsType[number] & { dataIndex: string } => 'dataIndex' in column && column.dataIndex !== 'key');

  const toggleView = React.useCallback(() => {
    if (pageSizeLimit === 'no-limit') {
      setPageSize(dataSource?.length);
    } else {
      setPageSize(pageSizeLimit);
    }
  }, [dataSource?.length, pageSizeLimit]);
  useEffect(() => {
    toggleView();
  }, [pageSizeLimit]);

  // Check if any item has children property for expandable rows
  const hasExpandableRows = dataSource.some(item => item.children && item.children.length > 0);

  return (
    <AntTable
      className={"commonTable" + (className ? ` ${className}` : 'mt-5')}
      dataSource={dataSource.slice(0, typeof pageSize === 'number' ? pageSize : dataSource.length)}
      columns={columns}
      pagination={false}
      loading={isFetching || isError}
      sticky={!!scrollY}
      scroll={{ x: 'max-content', y: scrollY || undefined }}
      expandable={hasExpandableRows ? {
        defaultExpandAllRows: false,
        expandRowByClick: true,
        indentSize: 20,
        expandIcon: ({ expanded, onExpand, record }) =>
          record.children && record.children.length > 0 ? (
            <button
              onClick={e => onExpand(record, e)}
              className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-all mr-2 border border-gray-300 hover:border-gray-400"
              title={expanded ? 'Collapse row' : 'Expand row'}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 14 14"
                fill="none"
                className={`transition-transform duration-200 flex-shrink-0 ${expanded ? 'rotate-180' : ''}`}
              >
                <path
                  d="M3.5 5.25L7 8.75L10.5 5.25"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="text-xs font-medium whitespace-nowrap">
                {expanded ? 'Hide' : 'Show'}
              </span>
            </button>
          ) : null,
      } : undefined}
      footer={typeof pageSizeLimit === 'number' && pageSizeLimit >= dataSource?.length ? undefined : () => {
        return (
          (!!dataSource?.length && exapandable) && (
            <Button
              disabled={isFetching}
              onClick={toggleView}
              icon={<DownloadIcon style={{ transform: pageSize === dataSource?.length ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.1s ease' }} />}
              iconPosition="end"
            >
              {pageSize === dataSource?.length ? 'Collapse Data' : 'View Complete Data'}
            </Button>
          )
        );
      }}
    />)
}

const TableTeam = ({ data, onEdit, onDelete, updateToggle }: TableTeamProps): JSX.Element => {
  const handleEdit = (id: string) => {
    try {
      onEdit(id);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = (id: string) => {
    try {
      onDelete(id);
    } catch (error) {
      console.error(error);
    }
  };

  const renderHeader = () => (
    <div className="flex items-center py-3 px-6 font-bold bg-gray-100 text-sm">
      <div className="basis-6/12">Name</div>
      <div className="basis-3/12">Email</div>
      <div className="basis-3/12">Admin</div>
    </div>
  );

  const renderRow = (row: TeamMember, index: number) => (
    <>
      <div
        key={index}
        className={`flex items-center px-6 py-4 bg-white border-b border-gray-200`}
      >
        <div className="flex items-center basis-6/12 gap-3">
          <img className="w-12 h-12 rounded-full" src={row?.profile_picture} alt={''} />
          <div className="text-[#181D27] text-sm font-medium">{row?.name}</div>
        </div>
        <div className="flex items-center basis-3/12">
          <div className="text-[#535862] text-sm font-normal">{row?.email}</div>
        </div>
        <div className="flex items-center basis-3/12">
          <Switch defaultChecked value={row?.admin} onChange={(checked: boolean) => {
            let payload = {
              "member_email": row?.email,
              "admin": checked
            }
            updateToggle(payload)
          }} className="teamSwitch" />
          <Button
            type="text"
            onClick={() => handleDelete(row.email)}
          >
            Delete
          </Button>
          {/* <Button
            color="primary"
            variant="text"
            onClick={() => handleEdit(row.id)}
          >
            Edit
          </Button> */}
        </div>
      </div>
      {index !== data.length - 1 && <div className="h-px bg-gray-200" />}
    </>
  );

  return (
    <div>
      <Table
        data={data}
        renderHeader={renderHeader}
        renderRow={renderRow}
      />
    </div>
  );
};

export { TableData, TableTeam };
export { DynamicTable } from './NestedTable';
